import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleNewsletter = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch('https://readdy.ai/api/form/d7dpm4pnoju8nk5a1e1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-stone-100 border-t border-stone-200">
      {/* Top Footer */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Brand */}
        <div>
          <img
            src="https://public.readdy.ai/ai/img_res/2c33ea10-b2d8-439b-9a97-77605099d973.png"
            alt="MyAbode"
            className="h-10 w-auto object-contain mb-4"
          />
          <p className="text-stone-500 text-sm leading-relaxed mb-5">
            Your trusted partner in finding the perfect home. Explore thousands of verified listings across premium locations worldwide.
          </p>
          {/* Newsletter */}
          <form
            data-readdy-form
            id="newsletter-form"
            onSubmit={handleNewsletter}
            className="flex gap-2"
          >
            <input
              type="email"
              name="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Your email"
              className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:border-amber-400"
              required
            />
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              {submitting ? '...' : 'Subscribe'}
            </button>
          </form>
          {submitted && <p className="text-amber-600 text-xs mt-2">Thanks for subscribing!</p>}
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-semibold text-stone-800 mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
          <ul className="space-y-2.5">
            {[
              { label: 'All Properties', to: '/listings' },
              { label: 'Buy a Home', to: '/listings?type=sale' },
              { label: 'Rent a Home', to: '/listings?type=rent' },
              { label: 'Dashboard', to: '/dashboard' },
              { label: 'Contact Us', to: '/contact' },
            ].map(link => (
              <li key={link.to}>
                <Link to={link.to} className="text-stone-500 hover:text-amber-600 text-sm transition-colors cursor-pointer">
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Resources */}
        <div>
          <h4 className="font-semibold text-stone-800 mb-4 text-sm uppercase tracking-wide">Resources</h4>
          <ul className="space-y-2.5">
            {['Mortgage Calculator', 'Area Guides', 'Legal Advice', 'FAQs', 'Blog'].map(item => (
              <li key={item}>
                <a href="#" className="text-stone-500 hover:text-amber-600 text-sm transition-colors cursor-pointer" rel="nofollow">
                  {item}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="font-semibold text-stone-800 mb-4 text-sm uppercase tracking-wide">Contact</h4>
          <ul className="space-y-3">
            <li className="flex items-start gap-2 text-stone-500 text-sm">
              <span className="w-4 h-4 flex items-center justify-center mt-0.5"><i className="ri-map-pin-line text-amber-500"></i></span>
              123 Property Lane, Suite 400<br />New York, NY 10001
            </li>
            <li className="flex items-center gap-2 text-stone-500 text-sm">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line text-amber-500"></i></span>
              +1 (800) 555-ABODE
            </li>
            <li className="flex items-center gap-2 text-stone-500 text-sm">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-mail-line text-amber-500"></i></span>
              hello@myabode.com
            </li>
          </ul>
          <div className="flex gap-3 mt-5">
            {['ri-instagram-line', 'ri-facebook-line', 'ri-linkedin-line', 'ri-twitter-x-line'].map(icon => (
              <a
                key={icon}
                href="#"
                rel="nofollow"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-200 hover:bg-amber-500 hover:text-white text-stone-500 transition-colors cursor-pointer"
              >
                <i className={`${icon} text-sm`}></i>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-stone-200 bg-stone-200/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-stone-500 text-xs">© 2026 MyAbode. All rights reserved.</p>
          <div className="flex gap-4">
            {['Privacy Policy', 'Terms of Service', 'Cookie Settings'].map(item => (
              <a key={item} href="#" rel="nofollow" className="text-stone-400 hover:text-stone-600 text-xs transition-colors cursor-pointer">
                {item}
              </a>
            ))}
          </div>
          <p className="text-stone-400 text-xs">Trusted by 50K+ users worldwide</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
